import makeStyles from '@mui/styles/makeStyles';
import {isEmpty} from 'lodash';
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {isEmailValid} from "common/helpers";
import {color, font, type} from "common/tokens";
import useBusy from "common/useBusy";
import OTP from "components/OTP";
import Question from 'components/Question/index';
import AuthLayout from "components/redesign/AuthLayout";
import CtaButton from "components/redesign/CtaButton";
import PasswordRules, {meetsPasswordPolicy} from "components/redesign/PasswordRules";

const useStyles = makeStyles({
  title: {
    ...type.panelTitle,
    color: color.TEXT,
    margin: 0,
    textAlign: 'center',
  },
  subtitle: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '6px 0 20px',
    textAlign: 'center',
  },
  gap: {
    height: 20,
  },
  progressTrack: {
    height: 4,
    borderRadius: 4,
    background: color.DIVIDER,
    overflow: 'hidden',
    margin: '0 0 20px',
  },
  progressFill: {
    display: 'block',
    height: '100%',
    borderRadius: 4,
    background: color.ACCENT,
  },
  error: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.DANGER,
    textAlign: 'center',
    margin: '14px 0 0',
  },
  link: {
    color: color.ACCENT_TEXT,
    fontWeight: font.SEMIBOLD,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    // Questions space themselves 28px apart for a survey; a short auth form
    // reads better tighter.
    '& > div': {
      marginBottom: 18,
    },
  },
});

const STEPS = 3;

const EmailVerification = ({
  authStep,
  verifyEmail,
  emailOtpError,
  otpVerificationStatus,
  validateOtp,
  setProfilePassword,
  otpError,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, run] = useBusy();

  const handleEmailSubmit = event => {
    if (event) event.preventDefault();

    if (isEmailValid(email)) {
      setEmailError(false);
      run(() => verifyEmail(email));
    } else {
      setEmailError(true);
    }
  };

  const passwordsMatch = !isEmpty(confirmPassword) && password === confirmPassword;
  const canSetPassword = meetsPasswordPolicy(password) && passwordsMatch;

  const steps = [
    (
      <>
        <h1 className={classes.title}>{t('emailEntryTitle')}</h1>
        <p className={classes.subtitle}>{t('emailEntrySubtitle')}</p>
        <form className={classes.form} onSubmit={handleEmailSubmit}>
          <Question
            question={{label: t('emailLabel'), questionType: 'Text', autocomplete: 'email', mandatory: true, helpText: t('emailHelpText')}}
            onAnswered={value => setEmail(value.toLowerCase())}
            value={email}
            error={emailError && t('invalidEmail')}
          />
          <CtaButton type={'submit'} size={'large'} fullWidth label={t('sendOTP')} loading={busy}/>
        </form>
        {emailOtpError && (
          <p className={classes.error} role={'alert'}>
            {`${t('emailAlreadySignedUp')} ${t('please')} `}
            <Link to={'/signin'} className={classes.link}>{t('login')}</Link>
            {` ${t('or')} `}
            <Link to={'/resetpassword'} className={classes.link}>{t('resetPassword')}</Link>
            {` ${t('ifForgotten')}`}
          </p>
        )}
      </>
    ),
    (
      <>
        <h1 className={classes.title}>{t('enterOTPToContinue')}</h1>
        <p className={classes.subtitle}>{`${t('sentOTP')} ${email}`}</p>
        <OTP
          onAnswered={otp => run(() => validateOtp(email, otp))}
          error={!otpVerificationStatus}
          resendOtp={() => run(() => verifyEmail(email))}
          currentStep={authStep === 1}
          otpErrorMessage={otpError}
          loading={busy}
        />
      </>
    ),
    (
      <>
        <h1 className={classes.title}>{t('passwordEntryTitle')}</h1>
        <div className={classes.gap}/>
        <form
          className={classes.form}
          onSubmit={event => {
            event.preventDefault();
            if (canSetPassword) run(() => setProfilePassword(password));
          }}
        >
          <Question
            question={{label: t('passwordLabelCaps'), questionType: 'Password', autocomplete: 'new-password', mandatory: true}}
            onAnswered={setPassword}
            value={password}
          />
          <PasswordRules password={password}/>
          <Question
            question={{label: t('confirmPasswordLabel'), questionType: 'Password', autocomplete: 'new-password', mandatory: true}}
            onAnswered={setConfirmPassword}
            value={confirmPassword}
            error={!isEmpty(confirmPassword) && !passwordsMatch && t('passwordsDontMatch')}
          />
          <CtaButton type={'submit'} size={'large'} fullWidth label={t('submit')} disabled={!canSetPassword} loading={busy}/>
        </form>
      </>
    ),
  ];

  return (
    <AuthLayout>
      <div
        className={classes.progressTrack}
        role={'progressbar'}
        aria-label={t('signUp')}
        aria-valuenow={authStep + 1}
        aria-valuemin={1}
        aria-valuemax={STEPS}
      >
        <span
          className={classes.progressFill}
          style={{width: `${((authStep + 1) * 100) / STEPS}%`}}
        />
      </div>
      {steps[authStep] || steps[0]}
    </AuthLayout>
  );
};

export default EmailVerification;
